import { useCallback, useState } from "react";
import type { MouseEvent } from "react";
import styled from "styled-components";
import { CollaborationInputControlsV2 } from "@/components/Collaboration/CollaborationChat/CollaborationInputControlsV2";
import CollaborationMessage from "@/components/Collaboration/CollaborationChat/CollaborationMessage";

import { SmallLoadingSpinner } from "ui/LoadingSpinner/LoadingSpinner";
import { ConfirmModal } from "ui/confirm-required/confirm-modal";
import { User } from "../../../features/user";
import { ColCollaborator, ColComment } from "../types";
import { useCollaborationChat } from "./hooks";

export interface CollaborationChatProps {
  channelId: string;
  user: User;
  postState: string;
  postId: string;
  disabled?: boolean;
}

interface ActiveThreadListProps {
  comments: ColComment[];
  collaborators: ColCollaborator[];
  currentUser: User;
  editingCommentId?: string;
  onDelete: (comment: ColComment) => void;
  onEdit: (comment: ColComment) => void;
  postState: string;
  disabled?: boolean;
}

const ActiveThreadList = ({
  comments,
  collaborators,
  currentUser,
  editingCommentId,
  onDelete,
  onEdit,
  postState,
  disabled,
}: ActiveThreadListProps) => {
  if (comments.length === 0) {
    return null;
  }

  return (
    <>
      {comments.map((comment, index) => (
        <CollaborationMessage
          key={comment._id}
          comment={comment}
          previousComment={index > 0 ? comments[index - 1] : undefined}
          nextComment={
            index < comments.length - 1 ? comments[index + 1] : undefined
          }
          collaborators={collaborators}
          currentUser={currentUser}
          onEdit={onEdit}
          onDelete={onDelete}
          isEditing={editingCommentId === comment._id}
          postState={postState}
          disabled={disabled}
        />
      ))}
    </>
  );
};

const CollaborationChat = ({
  channelId,
  user,
  postState,
  postId,
  disabled,
}: CollaborationChatProps) => {
  const {
    collaborators,
    editingComment,
    handleCancelEdit,
    handleMessageDeleted,
    handleMessageSubmit,
    handleStartEdit,
    isRequestingComments,
    messagesContainerRef,
    showEmptyState,
    threads,
    isError,
    clearError,
  } = useCollaborationChat({ channelId, user, postState, postId, disabled });

  const [commentPendingDeletion, setCommentPendingDeletion] =
    useState<ColComment | null>(null);

  const requestDelete = useCallback((comment: ColComment) => {
    setCommentPendingDeletion(comment);
  }, []);

  const handleCancelDelete = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      setCommentPendingDeletion(null);
    },
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (!commentPendingDeletion) {
      return;
    }

    void handleMessageDeleted(commentPendingDeletion);
    setCommentPendingDeletion(null);
  }, [commentPendingDeletion, handleMessageDeleted]);

  const mergedThreads = [...threads.legacy, ...threads.active];
  const hasCollaborators = collaborators.length > 0;
  const hasThreads = mergedThreads.length > 0;

  const isDeletingExternal = commentPendingDeletion?.visibility === "EXTERNAL";
  const deleteCopy = isDeletingExternal
    ? {
        title: "Delete external note",
        message:
          "This action cannot be undone. Once deleted, this external note will be permanently removed from the conversation.",
        confirm: "Delete external note",
      }
    : {
        title: "Delete internal comment",
        message:
          "This action cannot be undone. Once deleted, this internal comment will be permanently removed from the conversation.",
        confirm: "Delete internal comment",
      };

  return (
    <ChatContainer className="min-w-400px max-w-400px flex flex-col relative">
      {isRequestingComments && (
        <LoadingOverlay>
          <LoadingSpinnerStyled />
        </LoadingOverlay>
      )}

      <MessageListContainer ref={messagesContainerRef}>
        {hasCollaborators && hasThreads && (
          <ActiveThreadList
            comments={mergedThreads}
            collaborators={collaborators}
            currentUser={user}
            editingCommentId={editingComment?._id}
            onDelete={requestDelete}
            onEdit={handleStartEdit}
            postState={postState}
            disabled={disabled}
          />
        )}

        {showEmptyState && (
          <EmptyState>
            <EmptyStateTitle>Keep the Conversation Flowing</EmptyStateTitle>
            <EmptyStateDescription>
              This is where internal comments, external notes, and status
              updates live. Add one below to get the conversation started!
            </EmptyStateDescription>
            <EmptyStateCaret aria-hidden="true">
              <i className="fa-regular fa-chevron-down text-[14px] color-[#4f31ff]" />
            </EmptyStateCaret>
          </EmptyState>
        )}
      </MessageListContainer>
      <CollaborationInputControlsV2
        collaborators={collaborators}
        onMessageSend={handleMessageSubmit}
        editingComment={editingComment}
        disabled={disabled}
        currentUser={user}
        onCancelEdit={handleCancelEdit}
        isError={isError}
        onClearError={clearError}
      />

      <ConfirmModal
        maxWidth="552px"
        show={Boolean(commentPendingDeletion)}
        title={deleteCopy.title}
        message={deleteCopy.message}
        confirmText={deleteCopy.confirm}
        cancelText="Cancel"
        confirmType={{ alert: true }}
        onCancel={(event) => handleCancelDelete(event)}
        onConfirm={handleConfirmDelete}
      />
    </ChatContainer>
  );
};

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  z-index: 3;
`;

const ChatContainer = styled.div`
  --height: 620px;
  max-height: var(--height);
  height: var(--height);
`;

const MessageListContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N100, #e6e6e6);
  background: var(--Shade-White, #fff);
  display: flex;
  flex-direction: column;
  padding: 16px 0;

  /* scrollbar */
  ::-webkit-scrollbar {
    width: 10px;

    &-track {
      color: #fff;
      background: #f3f3f3;
      border-radius: 3px;
      box-shadow:
        inset 0 -4px 0 0,
        inset 0 4px 0 0;
    }

    &-corner {
      background: #fff;
    }

    &-thumb {
      background: #d9d9de;
    }

    &-button {
      display: none;
    }
  }
`;

const EmptyState = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  align-self: center;
  padding: 0 16px;
`;

const EmptyStateTitle = styled.h3`
  margin: 0;
  color: var(--text-text-brand2, #0e1680);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
`;

const EmptyStateDescription = styled.p`
  margin: 8px 0 16px 0;
  color: var(--text-text-primary, #333);
  text-align: center;
  /* Web App/P2 Regular (Text sm Regular) */
  font-family: var(--font-family-body, Inter);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const EmptyStateCaret = styled.div``;

const LoadingSpinnerStyled = styled(SmallLoadingSpinner)`
  width: 32px;
  height: 32px;
`;

export default CollaborationChat;
